// ===================================================================
// 💳 GERENCIADOR DE MÉTODOS DE PAGAMENTO - GRU, Boleto, etc.
// ===================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { useNotifications } from '../../contexts/NotificationContext';

const METHOD_TYPES = [
  { value: 'gru', label: 'GRU (PagTesouro)', desc: 'Instituições federais' },
  { value: 'boleto', label: 'Boleto bancário', desc: 'Instituições estaduais' }
];

export const PaymentMethodManager = React.memo(({ user }) => {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [stepType, setStepType] = useState(null); // null = selecionar tipo | 'gru' | 'boleto'
  const [formData, setFormData] = useState({
    name: '',
    method_type: 'gru',
    ug: '', codigo_servico: '', orgao_arrecadador: '', referencia_prefixo: '',
    banco: '', agencia: '', conta: '', convenio: '', carteira: '', cedente: '', cnpj: ''
  });
  const [error, setError] = useState('');
  const { addNotification } = useNotifications();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.getPaymentMethods();
      setMethods(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Erro ao carregar métodos de pagamento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleInputChange = useCallback((field, value) => {
    if (field === 'ug') value = (value || '').replace(/\D/g, '').slice(0, 6);
    if (['codigo_servico', 'orgao_arrecadador', 'banco', 'agencia'].includes(field)) value = (value || '').replace(/\D/g, '').slice(0, 10);
    if (field === 'referencia_prefixo') value = (value || '').replace(/\D/g, '').slice(0, 15);
    if (field === 'convenio') value = (value || '').replace(/\D/g, '').slice(0, 20);
    if (field === 'carteira') value = (value || '').replace(/\D/g, '').slice(0, 10);
    if (field === 'cnpj') value = (value || '').replace(/\D/g, '').slice(0, 14);
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  }, [error]);

  const buildPayload = useCallback(() => {
    const name = (formData.name || '').trim();
    const base = { name, method_type: formData.method_type };
    if (formData.method_type === 'gru') {
      return {
        ...base,
        ug: formData.ug || null,
        codigo_servico: formData.codigo_servico || null,
        orgao_arrecadador: formData.orgao_arrecadador || null,
        referencia_prefixo: formData.referencia_prefixo || null,
        banco: null, agencia: null, conta: null, convenio: null, carteira: null, cedente: null, cnpj: null
      };
    }
    return {
      ...base,
      banco: formData.banco || null,
      agencia: formData.agencia || null,
      conta: formData.conta || null,
      convenio: formData.convenio || null,
      carteira: formData.carteira || null,
      cedente: formData.cedente || null,
      cnpj: formData.cnpj || null,
      ug: null, codigo_servico: null, orgao_arrecadador: null, referencia_prefixo: null
    };
  }, [formData]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const name = (formData.name || '').trim();
    if (!name) { setError('Nome é obrigatório'); return; }
    if (formData.method_type === 'gru' && formData.ug && formData.ug.length < 6) {
      setError('Unidade Gestora deve ter 6 dígitos'); return;
    }

    try {
      setLoading(true);
      const payload = buildPayload();
      if (editingMethod) {
        const { error: updateError } = await apiClient.updatePaymentMethod(editingMethod.id, payload);
        if (updateError) setError(updateError);
        else {
          addNotification('Sucesso', 'Método de pagamento atualizado com sucesso', 'success');
          setShowModal(false); setEditingMethod(null); setStepType(null); loadData();
        }
      } else {
        const { error: createError } = await apiClient.createPaymentMethod(payload);
        if (createError) setError(createError);
        else {
          addNotification('Sucesso', 'Método de pagamento criado com sucesso', 'success');
          setShowModal(false); setEditingMethod(null); setStepType(null); loadData();
        }
      }
    } catch (err) {
      setError('Erro ao salvar método de pagamento');
    } finally {
      setLoading(false);
    }
  }, [formData, editingMethod, buildPayload, loadData, addNotification]);

  const handleEdit = useCallback((method) => {
    setEditingMethod(method);
    setStepType(method.method_type || 'gru');
    setFormData({
      name: method.name || '',
      method_type: method.method_type || 'gru',
      ug: method.ug || '', codigo_servico: method.codigo_servico || '', orgao_arrecadador: method.orgao_arrecadador || '', referencia_prefixo: method.referencia_prefixo || '',
      banco: method.banco || '', agencia: method.agencia || '', conta: method.conta || '', convenio: method.convenio || '', carteira: method.carteira || '', cedente: method.cedente || '', cnpj: method.cnpj || ''
    });
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(async (methodId) => {
    if (!window.confirm('Tem certeza que deseja excluir este método de pagamento?')) return;
    try {
      setLoading(true);
      const { error: deleteError } = await apiClient.deletePaymentMethod(methodId);
      if (deleteError) setError(deleteError);
      else { addNotification('Sucesso', 'Método de pagamento excluído com sucesso', 'success'); loadData(); }
    } catch (err) { setError('Erro ao excluir método'); }
    finally { setLoading(false); }
  }, [loadData, addNotification]);

  const handleNewMethod = useCallback(() => {
    setEditingMethod(null);
    setStepType(null);
    setFormData({
      name: '', method_type: 'gru',
      ug: '', codigo_servico: '', orgao_arrecadador: '', referencia_prefixo: '',
      banco: '', agencia: '', conta: '', convenio: '', carteira: '', cedente: '', cnpj: ''
    });
    setShowModal(true);
  }, []);

  const handleSelectType = useCallback((type) => {
    setStepType(type);
    setFormData(prev => ({ ...prev, method_type: type }));
  }, []);

  const getModalTitle = () => {
    if (editingMethod) return `Editar Método ${stepType === 'boleto' ? 'Boleto' : 'GRU'}`;
    if (stepType) return `Novo Método ${stepType === 'boleto' ? 'Boleto' : 'GRU'}`;
    return 'Novo método – Qual tipo deseja cadastrar?';
  };

  if (loading && methods.length === 0) {
    return <LoadingSpinner text="Carregando métodos de pagamento..." />;
  }

  return (
    <div style={styles.contentContainer}>
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>💳 Métodos de Pagamento</h2>
          <button onClick={handleNewMethod} style={{ ...styles.button, ...styles.buttonPrimary }}>➕ Novo Método</button>
        </div>
        {error && !showModal && <Alert message={error} type="error" onClose={() => setError('')} />}
        <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
          Cadastre GRU (federal) ou Boleto (estadual) e vincule ao processo em Configurações → Processos.
        </div>
        {methods.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>💳</div>
            <h3>Nenhum método cadastrado</h3>
            <p>Cadastre GRU ou Boleto para processos com taxa de inscrição</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th style={styles.tableHeaderCell}>Nome</th>
                  <th style={styles.tableHeaderCell}>Tipo</th>
                  <th style={styles.tableHeaderCell}>Detalhe</th>
                  <th style={styles.tableHeaderCell}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {methods.map(m => (
                  <tr key={m.id} style={styles.tableRow}>
                    <td style={styles.tableCell}><span style={{ fontWeight: '600', color: '#1F2937' }}>{m.name}</span></td>
                    <td style={styles.tableCell}>
                      <span style={{ ...styles.statusBadge, ...(m.method_type === 'boleto' ? styles.statusPending : styles.statusApproved) }}>
                        {m.method_type === 'boleto' ? 'Boleto' : 'GRU'}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      {m.method_type === 'boleto' ? (m.banco ? `Banco ${m.banco}` : '—') : (m.ug ? `UG ${m.ug}` : '—')}
                    </td>
                    <td style={styles.tableCell}>
                      <button onClick={() => handleEdit(m)} style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall }}>✏️ Editar</button>
                      <button onClick={() => handleDelete(m.id)} style={{ ...styles.button, ...styles.buttonDanger, ...styles.buttonSmall, marginLeft: 8 }}>🗑️ Excluir</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal isOpen={showModal} onClose={() => { setShowModal(false); setStepType(null); setEditingMethod(null); }} title={getModalTitle()} large>
        {error && <div style={{ marginBottom: '16px' }}><Alert message={error} type="error" onClose={() => setError('')} /></div>}

        {!stepType ? (
          <div style={{ padding: '16px 0' }}>
            <p style={{ marginBottom: 16, color: '#6B7280' }}>Selecione o tipo de método de pagamento que deseja cadastrar:</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {METHOD_TYPES.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => handleSelectType(t.value)}
                  style={{
                    padding: '16px 20px',
                    textAlign: 'left',
                    border: '1px solid #E5E7EB',
                    borderRadius: 8,
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: 15,
                    fontWeight: 600,
                    color: '#374151'
                  }}
                >
                  {t.label}
                  <div style={{ fontSize: 13, fontWeight: 400, color: '#6B7280', marginTop: 4 }}>{t.desc}</div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Nome *</label>
                <input type="text" value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} style={styles.input} placeholder={stepType === 'gru' ? 'Ex: GRU Federal - IFNMG' : 'Ex: Boleto UEMG'} required />
              </div>
            </div>

            {stepType === 'gru' && (
              <div style={{ marginTop: 16, padding: 12, backgroundColor: '#F9FAFB', borderRadius: 8 }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: 14, color: '#374151' }}>Configuração GRU (PagTesouro)</h4>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Unidade Gestora (UG)</label>
                    <input type="text" value={formData.ug} onChange={(e) => handleInputChange('ug', e.target.value)} style={styles.input} placeholder="Ex.: 153052" maxLength={6} />
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>6 dígitos</div>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Código do Serviço</label>
                    <input type="text" value={formData.codigo_servico} onChange={(e) => handleInputChange('codigo_servico', e.target.value)} style={styles.input} placeholder="Ex.: 020834" />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Órgão Arrecadador</label>
                    <input type="text" value={formData.orgao_arrecadador} onChange={(e) => handleInputChange('orgao_arrecadador', e.target.value)} style={styles.input} placeholder="Opcional" />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Prefixo Nº Referência</label>
                    <input type="text" value={formData.referencia_prefixo} onChange={(e) => handleInputChange('referencia_prefixo', e.target.value)} style={styles.input} placeholder="Opcional" />
                  </div>
                </div>
              </div>
            )}

            {stepType === 'boleto' && (
              <div style={{ marginTop: 16, padding: 12, backgroundColor: '#F9FAFB', borderRadius: 8 }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: 14, color: '#374151' }}>Configuração Boleto Bancário</h4>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Código do Banco</label>
                    <input type="text" value={formData.banco} onChange={(e) => handleInputChange('banco', e.target.value)} style={styles.input} placeholder="Ex.: 001 (BB), 237 (Bradesco)" />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Agência</label>
                    <input type="text" value={formData.agencia} onChange={(e) => handleInputChange('agencia', e.target.value)} style={styles.input} placeholder="Ex.: 1234" />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Conta</label>
                    <input type="text" value={formData.conta} onChange={(e) => handleInputChange('conta', e.target.value)} style={styles.input} placeholder="Conta + dígito" />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Convênio</label>
                    <input type="text" value={formData.convenio} onChange={(e) => handleInputChange('convenio', e.target.value)} style={styles.input} placeholder="6 ou 7 dígitos" />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Carteira</label>
                    <input type="text" value={formData.carteira} onChange={(e) => handleInputChange('carteira', e.target.value)} style={styles.input} placeholder="Ex.: 9" />
                  </div>
                  <div style={{ ...styles.formGroup, flex: '0 0 200px' }}>
                    <label style={styles.label}>CNPJ/CPF do Cedente</label>
                    <input type="text" value={formData.cnpj} onChange={(e) => handleInputChange('cnpj', e.target.value)} style={styles.input} placeholder="Somente números" maxLength={14} />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={{ ...styles.formGroup, flex: 1, minWidth: 0 }}>
                    <label style={styles.label}>Cedente (Nome da Instituição)</label>
                    <input type="text" value={formData.cedente} onChange={(e) => handleInputChange('cedente', e.target.value)} style={styles.input} placeholder="Nome completo da instituição beneficiária" />
                  </div>
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 8 }}>Geração de boleto em desenvolvimento. O cadastro já estará disponível.</div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
              <button type="button" onClick={() => setShowModal(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>Cancelar</button>
              <button type="submit" disabled={loading} style={{ ...styles.button, ...styles.buttonPrimary, opacity: loading ? 0.7 : 1 }}>{loading ? 'Salvando...' : (editingMethod ? 'Atualizar' : 'Criar')}</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
});
